import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'
import { cn } from '@/lib/utils'
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion'

interface InkRevealProps {
    text: string
    cms?: string
    className?: string
}

const SOFT_EDGE = 14

// A paragraph whose text darkens line by line as it scrolls through the
// viewport. The text node stays untouched, so it remains editable in place.
export const InkReveal: FC<InkRevealProps> = ({ text, cms, className }): ReactNode => {
    const ref = useRef<HTMLParagraphElement>(null)
    const reduced = usePrefersReducedMotion()
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start 0.9', 'end 0.5'] })

    const backgroundImage = useTransform(scrollYProgress, progress => {
        const ink = (reduced ? 1 : progress) * (100 + SOFT_EDGE) - SOFT_EDGE

        return `linear-gradient(176deg, var(--ink-strong) ${ink}%, var(--ink-soft) ${ink + SOFT_EDGE}%)`
    })

    return (
        <motion.p
            ref={ref}
            data-mdlfy-cms={cms}
            className={cn('ink-reveal', className)}
            style={{ backgroundImage }}
        >
            {text}
        </motion.p>
    )
}
